import { concat, partition, pipe, prop, sort, sortBy } from 'ramda';

import { TOURNAMENT_SS_WIDGETS } from './constants';
import { User } from '@/api/auth';
import { Tournament, TournamentBanner } from '@/api/cms/tournaments';
import { TopPlayer } from '@/api/tournaments';
import { filterByGroupAndDuration } from '@/helpers/cms';
import { sortByDate, timestamp } from '@/helpers/dateTime';

export enum TournamentMode {
  ACTIVE = 'active',
  FINISHED = 'finished',
  COMING_SOON = 'comingSoon',
}

export const getSSWidgetLink = (widgetId?: string) => {
  if (!widgetId) return '';
  return TOURNAMENT_SS_WIDGETS.replace('{}', widgetId);
};

const isAvailableTournament = (durationFrom: string, durationTo: string) => {
  const currentTimestamp = timestamp();

  const durationFromTimestamp = timestamp(durationFrom);
  const durationToTimestamp = timestamp(durationTo);

  return (
    currentTimestamp < durationFromTimestamp ||
    (durationFromTimestamp < currentTimestamp &&
      durationToTimestamp > currentTimestamp)
  );
};

interface UpdateTournamentsArgs {
  tournaments: Tournament[];
  user: User | undefined;
  showActive?: boolean;
}

export const updateTournaments = ({
  tournaments,
  user,
  showActive,
}: UpdateTournamentsArgs) => {
  const filteredByGroupAndSorted = pipe(
    filterByGroupAndDuration<Tournament>({ user }),
    sort((a, b) =>
      sortByDate<TournamentBanner>(a.banner, b.banner, 'durationTo')
    )
  )(tournaments);

  const [sortedActiveTournaments, sortedFinishedTournaments] = partition(
    ({ banner: { durationFrom, durationTo } }: Tournament) =>
      isAvailableTournament(durationFrom, durationTo),
    filteredByGroupAndSorted
  );

  const sortActiveTournamentsByOrder = (
    sortedActiveTournaments: Tournament[]
  ) => {
    const [withOrder, withoutOrder] = partition(
      ({ order }: Tournament) => order != null,
      sortedActiveTournaments
    );

    return concat(sortBy(prop('order'))(withOrder), withoutOrder);
  };

  if (showActive) return sortActiveTournamentsByOrder(sortedActiveTournaments);

  return [
    ...sortActiveTournamentsByOrder(sortedActiveTournaments),
    ...sortedFinishedTournaments,
  ];
};

enum TournamentStrategy {
  Bet = 'bet',
  Win = 'win',
  Rate = 'rate',
  Spin = 'spin',
  Points = 'points',
}

const mapTournamentPointsField: Record<TournamentStrategy, keyof TopPlayer> = {
  [TournamentStrategy.Bet]: 'bets',
  [TournamentStrategy.Win]: 'wins',
  [TournamentStrategy.Rate]: 'rate',
  [TournamentStrategy.Spin]: 'gamesTaken',
  [TournamentStrategy.Points]: 'points',
};

export const getPlayerTournamentPoints = (
  strategy: string,
  playerInfo: TopPlayer | undefined
): number => {
  const pointsField = mapTournamentPointsField[strategy as TournamentStrategy];

  return Number(playerInfo?.[pointsField]) || 0;
};
